/**
 * The route table — the one place a page's path and whether it is public are
 * declared.
 *
 * `src/App.tsx` attaches a component to each pattern; `vite.config.ts` reads the
 * public, parameter-free ones to know what to prerender and put in the sitemap.
 * Add a page here with `public: true` and both sides pick it up — there is no
 * second list to keep in step.
 */
import { POST_PATHS } from "./features/blog/catalogue";

export interface RouteDef {
  pattern: string;
  /** Public pages render signed out and are prerendered for search engines. */
  public?: boolean;
  /**
   * `false` keeps a public page out of the sitemap while still prerendering it.
   * For the pages that exist because the law says so and that nobody searches
   * for: they should answer a crawler that follows a footer link, and they
   * should not be one of the handful of URLs we put forward as the site.
   */
  index?: boolean;
}

/** Tested in order; the first match wins. */
export const ROUTES = [
  { pattern: "/", public: true },

  // One page per exam, and the page that chooses between them.
  { pattern: "/telc", public: true },
  { pattern: "/certyfikat", public: true },
  { pattern: "/porownanie", public: true },

  /**
   * One page per reason somebody needs a certificate. They live at the top
   * level, not under an exam: two of the three are served by either one, so
   * filing them under a single exam was the URL contradicting the page —
   * `/telc/studies` said telc while the page said both would do.
   */
  { pattern: "/karta-pobytu", public: true },
  { pattern: "/studia", public: true },
  { pattern: "/obywatelstwo", public: true },

  { pattern: "/pricing", public: true },
  /* For a school or a centre, not a candidate: linked from the footer only. */
  { pattern: "/dla-szkol", public: true },
  { pattern: "/dla-korepetytorow", public: true },

  /* What a candidate deals with before the exam: when they can sit it, and
     what they can practise on. Neither belongs to one exam — both compare the
     two — so they sit at the top level rather than under either. */
  { pattern: "/terminy", public: true },
  { pattern: "/arkusze", public: true },
  { pattern: "/slowka", public: true },
  /* The blog: a list, and one fixed URL per post. The post pattern takes a
     parameter so `App.tsx` maps one component to it; the URLs it may take are
     the slugs in `features/blog/catalogue.ts`, which the two lists below add —
     a parameterised route is otherwise never prerendered. */
  { pattern: "/poradnik", public: true },
  { pattern: "/poradnik/:slug", public: true },
  /* One rules page per exam, filed under the exam it explains. Two explicit
     patterns rather than `/telc/:page`, because a parameterised route cannot be
     prerendered — and these are the pages a candidate reads before they have an
     account.

     These moved from `/rules` and `/rules/certyfikat`, and no redirect was put
     in: the worker serves `/api/*` and nothing else, so there is no redirect
     mechanism to use, and with no inbound traffic and no external links there
     was nothing to send anywhere. Do not go looking for one. */
  { pattern: "/telc/zasady", public: true },
  { pattern: "/telc/zadania", public: true },
  { pattern: "/telc/list", public: true },
  { pattern: "/telc/opis-zdjecia", public: true },
  { pattern: "/telc/prezentacja", public: true },
  { pattern: "/certyfikat/zasady", public: true },
  /* A paper of its own per level, and the exam part a candidate searches for by
     name. Each is found from search, not from the header. */
  { pattern: "/certyfikat/b1", public: true },
  { pattern: "/certyfikat/b2", public: true },
  { pattern: "/certyfikat/zadania", public: true },
  { pattern: "/certyfikat/pisanie", public: true },
  { pattern: "/certyfikat/mowienie-b1", public: true },
  { pattern: "/certyfikat/mowienie-b2", public: true },
  { pattern: "/pisanie", public: true },
  { pattern: "/terms", public: true },
  { pattern: "/privacy", public: true },
  { pattern: "/cookies", public: true },
  /* Where to write. Public, because a question about paying comes before
     there is an account to sign in to. */
  { pattern: "/kontakt", public: true },
  { pattern: "/account" },
  { pattern: "/pass" },
  { pattern: "/app" },
  { pattern: "/set/:setId" },
  /* The oral exam belongs to a set, not to the app: it is one more part of the
     paper the candidate is working through. */
  { pattern: "/set/:setId/speaking" },
  /* The oral exam sat out loud and marked; its result lives at its own id. */
  { pattern: "/set/:setId/oral" },
  { pattern: "/oral/:sessionId" },
  { pattern: "/exam/:attemptId" },
  { pattern: "/practice/:attemptId" },
  { pattern: "/result/:attemptId" },
  { pattern: "/review/:attemptId" },
  { pattern: "/writing" },
  /* The operator's view. Not public, so never prerendered or in the sitemap;
     the Worker answers 404 to everyone but the addresses in `ADMIN_EMAILS`. */
  { pattern: "/admin" },
] as const satisfies readonly RouteDef[];

export type RoutePattern = (typeof ROUTES)[number]["pattern"];

/**
 * The public routes with no `:param` — the ones that can be prerendered as a
 * fixed URL. A public route that takes a parameter (a shareable result page,
 * say) would need its own way to enumerate the URLs to freeze.
 */
export const PRERENDER_ROUTES: string[] = ROUTES.filter(
  (r) => "public" in r && r.public && !r.pattern.includes(":"),
).map((r): string => r.pattern).concat(POST_PATHS);

/**
 * The prerendered routes that also go in the sitemap — everything except the
 * pages marked `index: false`. Two lists rather than one flag doing both jobs:
 * a cookie policy has to render for a crawler that follows the footer link,
 * and has no business being offered as a page worth indexing.
 */
export const SITEMAP_ROUTES: string[] = ROUTES.filter(
  (r) =>
    "public" in r &&
    r.public &&
    !r.pattern.includes(":") &&
    !("index" in r && r.index === false),
).map((r): string => r.pattern).concat(POST_PATHS);
